import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { IconCheckCircle } from '@components/icons';
import { NavigationContext } from '@contexts/navigation/navigation-context';
import { createLegacyUrl } from '@utils/create-legacy-url/create-legacy-url';
import classNames from 'classnames';

import { SidebarItemProps } from './sidebar-item.type';

export const SidebarItem = ({
  href,
  current,
  name,
  icon,
  hasCheck,
  isDisabled,
  isExternalUrl,
  children,
  isCollapsed,
  isImpersonatedUser,
  userEmail,
}: SidebarItemProps) => {
  const { preventRedirect, setIsItemClicked, setRedirectUrl } = useContext(NavigationContext);
  const renderIcon = () => {
    if (typeof icon === 'function') {
      const Icon = icon;
      return (
        <Icon
          data-testid='sidebar-item--icon'
          className={classNames(
            {
              'text-primary': current,
              'text-tertiary': !current,
            },
            'h-6 w-6 fill-current stroke-none'
          )}
        />
      );
    }
    return undefined;
  };

  const handleClick = (e: React.MouseEvent) => {
    const parsedHref = createLegacyUrl(href, isImpersonatedUser, userEmail);
    if (isExternalUrl) {
      e.preventDefault();
      window.open(parsedHref, '_self');
    }
    if (preventRedirect) {
      e.preventDefault();
      setRedirectUrl(href);
      setIsItemClicked(true);
    }
    return null;
  };

  return (
    <>
      <div className={classNames('flex items-center w-full group rounded-md hover:fill-secondary-active')}>
        <Link
          className={classNames('flex flex-row p-3 w-full', {
            'justify-between items-center w-full': hasCheck,
            'items-start': !hasCheck,
          })}
          style={isDisabled ? { pointerEvents: 'none' } : {}}
          onClick={(e) => handleClick(e)}
          to={href}>
          <div className='flex flex-row items-center'>
            <div className='relative self-start'>
              {renderIcon()}
              {hasCheck && (
                <IconCheckCircle
                  className={classNames('h-3.5 w-3.5', {
                    'text-subtitle-m text-primary': current,
                    'text-body-m text-secondary': !current,
                  })}
                />
              )}
            </div>
            {!isCollapsed && (
              <span
                className={classNames(
                  {
                    'text-subtitle-m text-primary': current,
                    'text-body-m text-secondary': !current,
                  },
                  'ml-3'
                )}>
                {name}
              </span>
            )}
          </div>
        </Link>
      </div>
      {children}
    </>
  );
};
